import { ACTIVITY_ACTION_LABELS, type InterventionStatus } from '@garage/shared';

/** Libellés des rôles et interrupteurs : ceux du contrat (vague 4). */
export { ROLE_LABELS, TOGGLE_LABELS } from '@garage/shared';

/**
 * Libellés et formats d'affichage (fr-FR). Aucun texte propre à un garage : uniquement des
 * libellés produit. Les dates sont affichées dans le fuseau indiqué (celui du garage), sinon
 * dans celui du navigateur.
 */

type DateOpts = Intl.DateTimeFormatOptions;
const cache = new Map<string, Intl.DateTimeFormat>();

/** Formateur fr-FR mis en cache ; `timeZone` absent = fuseau du navigateur. */
function formatter(opts: DateOpts, timeZone?: string): Intl.DateTimeFormat {
  const key = JSON.stringify([opts, timeZone ?? null]);
  let f = cache.get(key);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat('fr-FR', timeZone ? { ...opts, timeZone } : opts);
    } catch {
      f = new Intl.DateTimeFormat('fr-FR', opts); // fuseau inconnu : celui du navigateur
    }
    cache.set(key, f);
  }
  return f;
}

const DATE: DateOpts = { day: 'numeric', month: 'long', year: 'numeric' };
const SHORT: DateOpts = { day: 'numeric', month: 'short' };
const DATE_TIME: DateOpts = { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' };
const WEEKDAY: DateOpts = { weekday: 'long', day: 'numeric', month: 'long' };

/** Formatage avec l'ordinal français du premier du mois : « 1er octobre », jamais « 1 octobre ». */
function formatFr(opts: DateOpts, date: Date, tz?: string): string {
  return formatter(opts, tz)
    .formatToParts(date)
    .map((p) => (p.type === 'day' && p.value === '1' ? '1er' : p.value))
    .join('');
}

export const formatDate = (iso: string, tz?: string) => formatFr(DATE, new Date(iso), tz);
export const formatShortDate = (iso: string, tz?: string) => formatFr(SHORT, new Date(iso), tz);
export const formatDateTime = (iso: string, tz?: string) => formatFr(DATE_TIME, new Date(iso), tz);
export const formatWeekdayDate = (iso: string, tz?: string) => formatFr(WEEKDAY, new Date(iso), tz);

/** Jour en toutes lettres pour un titre de groupe : « Mercredi 1er octobre » (année si ce n'est pas l'année en cours). */
export function formatDayHeading(iso: string, tz?: string, now = new Date()): string {
  const sameYear = formatter({ year: 'numeric' }, tz).format(new Date(iso)) === formatter({ year: 'numeric' }, tz).format(now);
  const text = formatFr(sameYear ? WEEKDAY : { ...WEEKDAY, year: 'numeric' }, new Date(iso), tz);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const NBSP = String.fromCharCode(0xa0);
export const formatKm = (km: number) => `${new Intl.NumberFormat('fr-FR').format(km)}${NBSP}km`;

/** Libellés côté garage. */
export const STATUS_LABELS: Record<InterventionStatus, string> = {
  received: 'Réceptionné',
  in_progress: 'En cours',
  awaiting_decision: 'Attente décision client',
  awaiting_parts: 'Attente pièces',
  ready: 'Prêt',
  closed: 'Restitué',
};

/** Libellés côté client (portail), formulés pour un non-spécialiste. */
export const PORTAL_STATUS_LABELS: Record<InterventionStatus, string> = {
  received: 'Véhicule réceptionné',
  in_progress: 'Intervention en cours',
  awaiting_decision: 'En attente de votre décision',
  awaiting_parts: 'En attente de pièces',
  ready: 'Votre véhicule est prêt',
  closed: 'Véhicule restitué',
};

export const COMPLEMENT_STATUS_LABELS: Record<string, string> = {
  draft: 'Brouillon',
  pending_validation: 'À valider',
  sent: 'Envoyé au client',
  accepted: 'Accepté',
  refused: 'Refusé',
  postponed: 'Reporté',
  superseded: 'Remplacé',
  cancelled: 'Annulé',
  change_requested: 'Modification demandée',
};

export const DECISION_LABELS: Record<string, string> = {
  accepted: 'Accepté',
  refused: 'Refusé',
  postponed: 'Reporté',
  change_requested: 'Modification demandée',
};

export const CHANNEL_LABELS: Record<string, string> = {
  portal: 'sur le lien de suivi',
  phone: 'par téléphone',
  in_person: 'sur place',
};

export const DELIVERY_STATUS_LABELS: Record<string, string> = {
  queued: 'En attente',
  sent: 'Envoyé',
  failed: 'Échec',
  cancelled: 'Annulé',
};

export const KIND_LABELS: Record<string, string> = { part: 'Pièce', labor: 'Main-d’œuvre' };

/** Modèles d'e-mails (noms renvoyés par `deliveries.list`, lot L4). Nom inconnu : affiché tel quel. */
export const TEMPLATE_LABELS: Record<string, string> = {
  'notifications.intervention_received': 'Véhicule réceptionné',
  'notifications.photos_published': 'Nouvelles photos',
  'notifications.complement_sent': 'Travaux complémentaires à décider',
  'notifications.complement_decided': 'Décision du client (au garage)',
  'notifications.question_asked': 'Question du client (au garage)',
  'notifications.question_answered': 'Réponse du garage',
  'notifications.vehicle_ready': 'Véhicule prêt',
  'notifications.storage_reminder': 'Rappel avant l’échéance',
  'notifications.release_report': 'Compte rendu',
  'notifications.entry_report_shared': 'Fiche de réception',
  'notifications.visit_planned': 'Rendez-vous confirmé',
  'notifications.visit_reminder': 'Rappel de rendez-vous',
};

/**
 * Codes du journal (`interventions.activity`, lots L1 et L2). Code inconnu : affiché tel quel.
 * Vague 6 : vocabulaire « Travaux complémentaires » ; les libellés du contrat (`ACTIVITY_ACTION_LABELS`)
 * priment sur ceux d'ici.
 */
const LOCAL_ACTIVITY_LABELS: Record<string, string> = {
  'intervention.created': 'Entrée enregistrée',
  'intervention.updated': 'Dossier modifié',
  'intervention.ready': 'Véhicule prêt',
  'intervention.released': 'Véhicule restitué',
  'intervention.reopened': 'Dossier rouvert',
  'intervention.storage_waived': 'Gardiennage offert',
  'customer.created': 'Client créé',
  'customer.updated': 'Client modifié',
  'vehicle.created': 'Véhicule créé',
  'vehicle.updated': 'Véhicule modifié',
  'media.uploaded': 'Photo ajoutée',
  'media.caption_updated': 'Légende modifiée',
  'media.published': 'Photos publiées',
  'media.unpublished': 'Photo retirée du suivi',
  'media.deleted': 'Photo supprimée',
  'complement.created': 'Travaux complémentaires préparés',
  'complement.submitted': 'Travaux complémentaires soumis à validation',
  'complement.sent': 'Travaux complémentaires envoyés au client',
  'complement.cancelled': 'Travaux complémentaires annulés',
  'complement.decided': 'Décision du client',
  'complement.decision_recorded': 'Décision transmise au garage',
  'question.asked': 'Question du client',
  'message.answered': 'Réponse envoyée',
  'message.handled': 'Question traitée',
  'work_item.created': 'Travail ajouté',
  'work_item.updated': 'Travail modifié',
  'work_item.deleted': 'Travail retiré',
  'portal.link_created': 'Lien de suivi partagé',
  'portal.links_revoked': 'Liens de suivi désactivés',
  'portal.report_link_created': 'Compte rendu partagé',
  'portal.report_link_revoked': 'Lien du compte rendu révoqué',
  'intervention.ready_cancelled': 'Mise à disposition annulée',
  'complement.updated': 'Travaux complémentaires modifiés',
  'complement.revision_opened': 'Nouvelle version des travaux complémentaires en préparation',
  'complement.cancelled_at_release': 'Travaux complémentaires annulés à la sortie',
  'media.published_with_complement': 'Photos publiées avec les travaux complémentaires',
  'entry_report.validated': 'Fiche de réception validée',
  'entry_report.shared': 'Fiche de réception envoyée au client',
  'entry_report.acknowledged': 'Fiche de réception lue par le client',
};

export const ACTIVITY_LABELS: Record<string, string> = { ...LOCAL_ACTIVITY_LABELS, ...ACTIVITY_ACTION_LABELS };

/**
 * C3 : « de » devant un nom propre venu des réglages (garage, produit), avec élision et contraction :
 * « d’Atelier Martin », « de Garage Sud », « du Garage de la Gare » (« Le … »), « des Ateliers … »
 * (« Les … »), « de l’Atelier … » (« L’… »).
 */
export function ofName(name: string): string {
  const n = name.trim();
  const lower = n.toLowerCase();
  if (lower.startsWith('le ')) return `du ${n.slice(3)}`;
  if (lower.startsWith('les ')) return `des ${n.slice(4)}`;
  if (/^l[’']/i.test(n)) return `de l’${n.slice(2)}`;
  if (/^[aeiouyhàâäéèêëîïôöûüœæ]/i.test(n)) return `d’${n}`;
  return `de ${n}`;
}
